import 'core-js';
import '@babel/polyfill';

import Vue from 'vue';
import AsyncComputed from 'vue-async-computed';
import { Fragment } from 'vue-frag';
import BootstrapVue from 'bootstrap-vue';
import VueAccessibleModal from 'vue-accessible-modal';
import VueGtag from 'vue-gtag';
import Storage from 'vue-web-storage';
import VueResource from 'vue-resource';
import { sync } from 'vuex-router-sync';
import Keycloak from 'keycloak-js';
import UserClient from '@/api/UserClient';
import StudentClient from '@/api/StudentClient';
import PlatformClient from '@/api/PlatformClient';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import fontAwesomeIcons from './constants/fontAwesomeIcons';

import DefaultLayout from '@/layouts/Default.vue';
import NoHeaderLayout from '@/layouts/NoHeader.vue';
import DashboardLayout from '@/layouts/Dashboard.vue';
import SurveyLayout from '@/surveys/SurveyLayout.vue';
import CurrentUser from './currentUser';

import router from './router';
import store from './store';
import filters from './filters';
import App from './App.vue';

window.betterMynd = window.betterMynd || {};
window.betterMynd.version = process.env.VUE_APP_GIT_VERSION;
window.betterMynd.apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

// TODO remove v2.bettermynd.com
if (window.location.hostname === 'v2.bettermynd.com') {
  window.location.assign(
    window.location
      .toString()
      .replace('v2.bettermynd.com', 'app.bettermynd.com')
  );
}

Vue.prototype.$globalEnv = {
  baseUrl: process.env.VUE_APP_API_BASE_URL || '',
  baseUrlV2: process.env.VUE_APP_API_BASE_URL_V2 || '',
  meetingsUrl: process.env.VUE_APP_MEETINGS_URL || '',
  stripe: process.env.VUE_APP_STRIPE || '',
  cronofyClientID: process.env.VUE_APP_CRONOFY_CLIENT_ID || ''
};

Vue.component('Fragment', Fragment);
Vue.use(BootstrapVue);
Vue.use(filters);
Vue.use(Storage);
Vue.use(AsyncComputed);
Vue.use(VueResource);
Vue.use(VueAccessibleModal, { transition: 'fade' });
Vue.config.productionTip = false;

Vue.use(
  VueGtag,
  {
    config: { id: 'G-9P46HGNRJE' },
    appName: 'BetterMynd - Web',
    pageTrackerScreenviewEnabled: true
  },
  router
);

const clarityId = process.env.VUE_APP_CLARITY_ID;
if (clarityId) {
  const l = document;
  const r = 'script';
  const t = l.createElement(r);
  t.async = 1;
  t.src = `https://www.clarity.ms/tag/${clarityId}`;

  const y = l.getElementsByTagName(r)[0];
  y.parentNode.insertBefore(t, y);
}

library.add(fontAwesomeIcons);
Vue.component('font-awesome-icon', FontAwesomeIcon);

Vue.component('default-layout', DefaultLayout);
Vue.component('no-header-layout', NoHeaderLayout);
Vue.component('dashboard-layout', DashboardLayout);
Vue.component('survey-layout', SurveyLayout);

const keycloak = new Keycloak({
  url: process.env.VUE_APP_KEYCLOAK_URL || 'https://login-dev.bettermynd.com',
  realm: 'BetterMynd',
  clientId: 'bettermynd-web'
});

Vue.prototype.$globals = new Vue({
  data() {
    return {
      userClient: new UserClient(this.$http),
      platformClient: new PlatformClient(this.$http),
      studentClient: new StudentClient(this.$http),

      features: null,
      schoolFeatures: null,

      unreadMessages: 0,
      notifications: [],
      
      view:{
        innerWidth: window.innerWidth,
        outerWidth: window.outerWidth,
        innerHeight: window.innerHeight,
        outerHeight: window.outerHeight,
      },

      lastActivityDate: null,
      lastActivityTimeout: null,

      keycloak,
      store
    };
  },

  created() {
    window.addEventListener('resize', this.handleResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },

  computed: {
    isMobile() {
      return this.view.innerWidth < 992;
    }
  },

  methods: {
    handleResize() {
      this.view.innerWidth = window.innerWidth;
      this.view.outerWidth = window.outerWidth;
      this.view.innerHeight = window.innerHeight;
      this.view.outerHeight = window.outerHeight;
    },

    logout() {
      if (!keycloak.authenticated || keycloak.isTokenExpired()) {
        keycloak.clearToken();
        keycloak.login({});
      } else {
        this.keycloak.logout({ redirectUri: window.location.origin });
      }
    },

    keepSessionAlive() {
      const delta = 12 * 60 * 60e3; // 4hr

      if (this.lastActivityTimeout) {
        clearTimeout(this.lastActivityTimeout);
      }
      // if we dont route change in this duration, force a logout
      this.lastActivityTimeout = setTimeout(() => this.logout(), delta);
    },

    async updateRemainingCredits() {
      if(!store.getters.currentUser.is_student)
        return;
      
      try {
        const response = await this.studentClient.getRemainingCredits();
        store.getters.currentUser.student_profile.credits_remaining = { ...response }
      } catch (ex) {
        console.error(ex);
        store.getters.currentUser.student_profile.credits_remaining = {
          individual_session_credits_remaining: 0,
          group_session_credits_remaining: 0,
        }
      }
    },

    async getUnreadCounts(user) {
      if (user) {
        this.unreadMessages =
          user._unread_messages_count >= 10
            ? '9+'
            : user._unread_messages_count;
        return;
      }

      if (!this.userClient || !store.getters.currentUser) {
        return;
      }

      try {
        count = await this.userClient.getUnreadMessageCount();
        if(count > 9)
          this.unreadMessages = "9+"
        else
          this.unreadMessages = count;
      } catch (ex) {
        this.unreadMessages = 0;
      }
    },

    async hasFeature(flag) {
      if (!store.getters.currentUser) {
        return false;
      }

      if (this.features === null) {
        await this.loadFeatures();
      }

      return !!this.features.find((x) => x === flag);
    },

    // Use when you're a BetterMyndAdmin
    async schoolHasFeature(flag, schoolId) {
      if (this.schoolFeatures === null) {
        const response = await this.platformClient.getFeatures({ schoolId })
        this.schoolFeatures = response;
      }
      return !!this.schoolFeatures.find(x => x === flag);
    },

    async loadFeatures() {
      let response = { names: [] };

      if (store.getters.currentUser.is_college_admin) {
        const { school } = store.getters.currentUser;
        response = await this.platformClient.getFeatures({
          schoolId: school.id
        });
      } else if (store.getters.currentUser.is_counselor) {
        const counselor = store.getters.currentUser.counselor_profile;
        response = await this.platformClient.getFeatures({
          counselorId: counselor.id
        });
      } else if (store.getters.currentUser.is_student) {
        const user = store.getters.currentUser;
        response = await this.platformClient.getFeatures({
          studentUserId: user.id
        });
      }

      this.features = response;
    }
  }
});

sync(store, router, { moduleName: 'RouteModule' });

Vue.prototype.$user = new Vue(CurrentUser(store));

const app = new Vue({
  router,
  store,
  render: (h) => h(App)
});

let kcLogin = keycloak.login;
keycloak.login = (options) => {
  if(!options)
    options = { idpHint: null }

  const { idp } = router.currentRoute.query;
  if(idp)
    options.idpHint = idp;
    
  kcLogin(options);
};

keycloak
  .init({ checkLoginIframe: false })
  .then(async (authenticated) => {
    if (app.$router.currentRoute.path === '/register')
      keycloak.register({ redirectUri: window.location.origin })
    else if (authenticated && !keycloak.isTokenExpired())
      app.$mount('#app');
    else  
      keycloak.login()
  });

setInterval(() => {
  if (!keycloak.authenticated || keycloak.isTokenExpired()) {
    keycloak.clearToken();
    keycloak.login({});
  } else {
    keycloak.updateToken(70)
      .catch(reason => keycloak.login());
  }
}, 6000);
